import { get } from 'lodash'
import Axios from 'axios'

const batchingDelay = 100
const debug = false

const HTTP_STATUS_CODES = {
  'CODE_200': 'OK',
  'CODE_201': 'Created',
  'CODE_202': 'Accepted',
  'CODE_203': 'Non-Authoritative Information',
  'CODE_204': 'No Content',
  'CODE_205': 'Reset Content',
  'CODE_206': 'Partial Content',
  'CODE_300': 'Multiple Choices',
  'CODE_301': 'Moved Permanently',
  'CODE_302': 'Found',
  'CODE_303': 'See Other',
  'CODE_304': 'Not Modified',
  'CODE_305': 'Use Proxy',
  'CODE_307': 'Temporary Redirect',
  'CODE_400': 'Bad Request',
  'CODE_401': 'Unauthorized',
  'CODE_402': 'Payment Required',
  'CODE_403': 'Forbidden',
  'CODE_404': 'Not Found',
  'CODE_405': 'Method Not Allowed',
  'CODE_406': 'Not Acceptable',
  'CODE_407': 'Proxy Authentication Required',
  'CODE_408': 'Request Timeout',
  'CODE_409': 'Conflict',
  'CODE_410': 'Gone',
  'CODE_411': 'Length Required',
  'CODE_412': 'Precondition Failed',
  'CODE_413': 'Request Entity Too Large',
  'CODE_414': 'Request-URI Too Long',
  'CODE_415': 'Unsupported Media Type',
  'CODE_416': 'Requested Range Not Satisfiable',
  'CODE_417': 'Expectation Failed',
  'CODE_500': 'Internal Server Error',
  'CODE_501': 'Not Implemented',
  'CODE_502': 'Bad Gateway',
  'CODE_503': 'Service Unavailable',
  'CODE_504': 'Gateway Timeout',
  'CODE_505': 'HTTP Version Not Supported'
}

export default class Requests {
  constructor (carrier) {
    this.carrier = carrier
    this.queue = []

    this.dispatching = false
  }

  push ({ method, path, options }) {
    options = {
      params: undefined,
      data: undefined,
      timeout: 10000,
      immediate: false,
      ...options
    }

    if (options.immediate) {
      return this._computeRequest({ method, path, options })
    }

    let resolve, reject
    const promise = new Promise((resolve1, reject1) => {
      resolve = resolve1
      reject = reject1
    })

    if (debug) console.log('Pushing request', method, path, 'into queue of', this.queue.length, 'items ...')
    this.queue.push({
      method,
      path,
      options,
      resolve,
      reject
    })

    if (!this.dispatching) {
      this.dispatching = true
      setTimeout(() => {
        this.dispatch()
      }, batchingDelay)
    }

    return promise
  }

  async _computeRequest ({ method, path, options, resolve, reject } = { options: {} }) {
    this.carrier._vm.loading++

    try {
      const response = await this.carrier.axios({
        url: path,
        method: method,
        params: options.params || undefined,
        data: options.payload || undefined,
        timeout: options.timeout || 10000
      })

      this.carrier._vm.loading--

      const result = {
        status: response.status,
        data: get(response, 'data', undefined)
      }

      if (resolve) resolve(result)
      return result
    } catch (error) {
      this.carrier._vm.loading--

      const rejection = this._toRejection(get(error, 'request.status', null))
      this.carrier.emit('error', 'Could not fetch ' + path + ' : '+ rejection.message)
      reject(rejection)
    }
  }

  async _computeRequests (requests) {
    this.carrier._vm.loading++

    const response = await this.carrier.axios({
      url: '/batch',
      method: 'post',
      data: {
        tasks: requests.map(request => {
          return {
            method: request.method,
            path: request.path,
            query: get(request, 'options.params', undefined),
            payload: get(request, 'options.payload', undefined),
          }
        })
      }
    })

    let i = 0
    for (const result of response.data) {
      if (result.code === 200) {
        requests[i].resolve({ status: result.code, data: result.payload })
      } else if (result.code === 301) {
        this.carrier._vm.loading++
        try {
          const redirect = await Axios.get(result.headers.Location)
          requests[i].resolve({ status: result.code, data: redirect.data })
        } catch (e) {
          const rejection = this._toRejection(get(e, 'request.status', null))
          this.carrier.emit('error', 'Could not fetch ' + requests[i].path + ' : '+ rejection.message)
          requests[i].reject(rejection)
        } finally {
          this.carrier._vm.loading--
        }
      } else {
        const rejection = this._toRejection(result.code)
        this.carrier.emit('error', 'Could not fetch ' + requests[i].path + ' : '+ rejection.message)
        requests[i].reject(rejection)
      }
      i++
    }

    this.carrier._vm.loading--
  }

  dispatch () {
    if (debug) console.log('Running dispatch of', this.queue.length, 'items ...')

    const requests = this.queue
    this.queue = []
    this.dispatching = false

    if (requests.length > 1) {
      return this._computeRequests(requests)
    } else if (requests.length === 1) {
      return this._computeRequest(requests[0])
    }
  }

  _toRejection (code, message) {
    if(!code && !message) {
      message = 'Network request error.'
    }

    return {
      code,
      message: message ? message : code + ' ' + HTTP_STATUS_CODES['CODE_' + code]
    }
  }
}